import { type ReactNode } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, BarChart3, Bell, History, Package } from 'lucide-react';

import { Card } from '@/components/ui/Card';

/**
 * Shared chrome for the four ShipEdge Reports admin pages.
 * Phase 15 Day 7. Components only — the access-gate hook lives in
 * `_gate.ts` so this file plays nicely with Vite's react-refresh rule.
 */

export function ShipedgeAccessDenied() {
  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <Link
        to="/dashboard"
        className="inline-flex items-center gap-1 text-[13px] text-kw-muted hover:text-kw-shark"
      >
        <ArrowLeft className="size-4" />
        Back to dashboard
      </Link>
      <Card className="flex items-start gap-3 p-4">
        <AlertTriangle className="size-5 shrink-0 text-kw-warning-text" />
        <div>
          <p className="text-[14px] font-medium text-kw-shark">Superadmin access required</p>
          <p className="mt-1 text-[13px] text-kw-muted">
            ShipEdge Reports are visible to visionary and integrator roles. Ask one of them for access if you
            need it.
          </p>
        </div>
      </Card>
    </div>
  );
}

const NAV_TABS = [
  {
    href: '/admin/shipedge/customer-profitability',
    label: 'Customer Profitability',
    icon: BarChart3,
  },
  { href: '/admin/shipedge/operational-mix', label: 'Operational Mix', icon: Bell },
  { href: '/admin/shipedge/lot-lookup', label: 'Lot Lookup', icon: Package },
  { href: '/admin/shipedge/sync-log', label: 'Sync Log', icon: History },
] as const;

export function ShipedgePageHeader({
  title,
  description,
  actions,
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
}) {
  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-1 text-[12px] text-kw-muted hover:text-kw-shark"
          >
            <ArrowLeft className="size-3.5" />
            Back to dashboard
          </Link>
          <h1 className="typo-h1 mt-1">{title}</h1>
          {description ? <p className="mt-1 text-[14px] text-kw-muted">{description}</p> : null}
        </div>
        {actions ? <div className="flex flex-wrap gap-2">{actions}</div> : null}
      </div>
      <nav className="flex flex-wrap gap-1 border-b border-kw-border">
        {NAV_TABS.map((tab) => (
          <NavLink
            key={tab.href}
            to={tab.href}
            className={({ isActive }) =>
              `relative flex items-center gap-2 px-3 py-2 text-[13px] ${
                isActive ? 'text-kw-shark' : 'text-kw-muted hover:text-kw-shark'
              }`
            }
          >
            {({ isActive }) => (
              <>
                <tab.icon className="size-4" />
                {tab.label}
                {isActive ? <span className="absolute inset-x-0 -bottom-px h-0.5 bg-kw-cerulean" /> : null}
              </>
            )}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
